/* eslint-disable */
import type { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import { GetServerSidePropsContext } from 'next';
import axios, { AxiosHeaders, CreateAxiosDefaults } from 'axios';

import { logger } from '../lib/logger';
import { isClient, isServer, isTest } from '../utils/environments';

type ApiConfig = CreateAxiosDefaults & {
  enableRetry?: boolean;
};

export class ApiBuilder {
  private static instance: ApiBuilder;

  private session: Session | null = null;

  private constructor() {
    if (isClient() && !isTest()) {
      getSession().then(session => {
        this.session = session;
      });
    }
  }

  public static getInstance(): ApiBuilder {
    if (!ApiBuilder.instance) {
      ApiBuilder.instance = new ApiBuilder();
    }

    return ApiBuilder.instance;
  }

  private getAuthTokenFromSession(session: Session | null | undefined) {
    const px = session?.user?.px;
    return px?.tk ?? session?.user?.tk ?? null;
  }

  private getRefreshTokenFromSession(session: Session | null | undefined) {
    const px = session?.user?.px;
    return px?.rtk ?? session?.user?.rtk ?? null;
  }

  public mount(config: ApiConfig = {}, context?: GetServerSidePropsContext) {
    const { enableRetry = false, ...axiosConfig } = config;

    const api = axios.create({
      ...axiosConfig,
      withCredentials: true,
      paramsSerializer: { indexes: null },
    });

    if (!isTest()) {
      api.interceptors.request.use(async requestConfig => {
        if (isClient() || context) {
          let session;

          if (context) {
            session = await getSession(context);
          } else {
            session = await getSession();
          }

          if (session) {
            this.session = session;
            requestConfig.headers = (requestConfig.headers ??
              {}) as AxiosHeaders;

            const existingAuthorization =
              requestConfig.headers.get('Authorization');
            const hasValidAuthorization =
              typeof existingAuthorization === 'string' &&
              existingAuthorization.startsWith('Bearer ') &&
              existingAuthorization.length > 'Bearer '.length &&
              !existingAuthorization.endsWith('undefined') &&
              !existingAuthorization.endsWith('null');

            if (!hasValidAuthorization) {
              const token = this.getAuthTokenFromSession(session);
              if (token) {
                requestConfig.headers.setAuthorization(`Bearer ${token}`);
                const refreshToken = this.getRefreshTokenFromSession(session);
                if (refreshToken) {
                  requestConfig.headers.RefreshToken = refreshToken;
                }
              }
            }
          }
        }

        return requestConfig;
      });

      // automatic retry with token refresh (only if enableRetry is active)
      if (enableRetry) {
        api.interceptors.response.use(
          response => response,
          async error => {
            const originalRequest = error.config;

            if (originalRequest._retry) {
              return Promise.reject(error);
            }

            if (error.response?.status === 401) {
              originalRequest._retry = true;

              if (
                typeof originalRequest.url === 'string' &&
                originalRequest.url.includes('v2/auth/proxy')
              ) {
                return Promise.reject(error);
              }

              if (isClient() && !originalRequest._isSSR) {
                if (window.location.pathname === '/auth/redirect') {
                  return Promise.reject(error);
                }

                try {
                  const refreshResponse = await fetch('/api/auth/refresh', {
                    method: 'POST',
                    credentials: 'include',
                  });

                  if (refreshResponse.ok) {
                    const session = await getSession();
                    if (session) {
                      const px = session.user?.px;
                      const activeToken = px?.tk ?? session.user?.tk;
                      originalRequest.headers.setAuthorization(
                        `Bearer ${activeToken}`
                      );
                    }

                    return api(originalRequest);
                  }
                } catch (refreshError) {
                  console.error('[ApiBuilder] - Refresh failed:', refreshError);
                }

                window.location.href = '/auth/redirect';
              } else {
                // SSR: redirect to auth preserving the original URL as callbackUrl
                try {
                  const { redirect } = await import('next/navigation');
                  const { headers } = await import('next/headers');
                  const headersList = await headers();
                  const pathname = headersList.get('x-pathname') ?? '/';
                  const search = headersList.get('x-search') ?? '';

                  if (pathname.startsWith('/auth/')) {
                    logger.warn(
                      '[ApiBuilder] SSR 401 on auth route - skipping redirect',
                      {
                        source: 'ApiBuilder.interceptor',
                        pathname,
                      }
                    );
                    return Promise.reject(error);
                  }

                  logger.warn('[ApiBuilder] SSR 401 - redirecting to auth', {
                    source: 'ApiBuilder.interceptor',
                    url: originalRequest.url,
                    callbackUrl: `${pathname}${search}`,
                  });

                  const callbackUrl = encodeURIComponent(
                    `${pathname}${search}`
                  );
                  redirect(`/auth/redirect?callbackUrl=${callbackUrl}`);
                } catch (redirectError) {
                  const { isRedirectError } = await import(
                    'next/dist/client/components/redirect-error'
                  );
                  if (isRedirectError(redirectError)) {
                    throw redirectError;
                  }
                  logger.error(
                    '[ApiBuilder] SSR 401 - failed to redirect, falling back to reject',
                    {
                      source: 'ApiBuilder.interceptor',
                      error: redirectError,
                    }
                  );
                  return Promise.reject(error);
                }
              }
            }

            return Promise.reject(error);
          }
        );
      }
    }
    return api;
  }
}

export const genomeApi = ApiBuilder.getInstance().mount({
  baseURL: process.env.NEXT_PUBLIC_GENOME_API_URL,
  enableRetry: true,
});

// For NextAuth callbacks and auth route handlers: no 401 redirect interceptor.
export const genomeApiServer = ApiBuilder.getInstance().mount({
  baseURL: process.env.NEXT_PUBLIC_GENOME_API_URL,
  timeout: 10000,
  enableRetry: false,
});

export const translationApi = ApiBuilder.getInstance().mount({
  baseURL: process.env.NEXT_PUBLIC_TRANSLATION_API_URL,
  enableRetry: false,
});

export const peoplePlatformApi = ApiBuilder.getInstance().mount({
  baseURL: process.env.NEXT_PUBLIC_PEOPLE_PLATFORM_API_URL,
  enableRetry: false,
});

export const authApiPublic = ApiBuilder.getInstance().mount({
  baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
  enableRetry: false,
});

export const authApiInternal = ApiBuilder.getInstance().mount({
  baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL_INTERNAL,
  enableRetry: false,
});

export const photoApi = ApiBuilder.getInstance().mount({
  baseURL: process.env.NEXT_PUBLIC_PEOPLE_PLATFORM_PHOTO_API_URL,
  responseType: 'blob',
  enableRetry: false,
});

// eslint-disable-next-line func-names
export const authApi = function () {
  if (isServer()) {
    return authApiInternal;
  }
  return authApiPublic;
};
